import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import UserAvatar from "../components/UserAvatar";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Heart, MessageCircle, Calendar, Sparkles, Home, Search, Plus, Bell, User } from "lucide-react";
import api from "../lib/api";

function formatRelativeTime(iso) {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const day = Math.floor(diff / 86400000);
  if (day < 1) return "today";
  if (day === 1) return "yesterday";
  return `${day}d ago`;
}

const STRENGTH_ICONS = { close: { emoji: "🔥", label: "Close", color: "text-orange-500 bg-orange-500/10" }, regular: { emoji: "💚", label: "Regular", color: "text-emerald-500 bg-emerald-500/10" }, distant: { emoji: "💤", label: "Distant", color: "text-muted-foreground/70 bg-muted/40" }, new: { emoji: "✨", label: "New", color: "text-cyan-400 bg-cyan-500/10" } };

export default function RelationshipsPage() {
  const navigate = useNavigate();
  const [relationships, setRelationships] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    api.get("/profile/me").then((r) => setProfile(r.data)).catch(() => {});
    Promise.all([api.get("/relationships"), api.get("/relationships/reminders"), api.get("/relationships/insights")])
      .then(([r, rem, ins]) => { setRelationships(r.data); setReminders(rem.data); setInsights(ins.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen pb-20 lg:pb-0" data-testid="relationships-page">
      <div className="sticky top-0 z-30 nav-glass px-4 h-12 flex items-center gap-3">
        <button onClick={() => navigate("/feed")} className="text-muted-foreground hover:text-foreground/80 bg-transparent border-0 cursor-pointer"><ArrowLeft className="w-5 h-5" /></button>
        <Heart className="w-4 h-4 text-pink-500" />
        <h1 className="font-outfit text-base font-semibold text-foreground">Relationships</h1>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 text-cyan-400 animate-spin" /></div>
        ) : (
          <>
            {/* AI Insights */}
            {insights && insights.insights.length > 0 && (
              <div className="bg-gradient-to-br from-cyan-500/10 to-violet-500/10 border border-cyan-500/20 rounded-xl p-5" data-testid="relationship-insights">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles className="w-4 h-4 text-cyan-400" />
                  <h2 className="text-sm font-semibold text-foreground">Insights</h2>
                </div>
                <div className="space-y-1.5">
                  {insights.insights.map((ins, i) => (
                    <p key={i} className="text-xs text-foreground/80">{ins}</p>
                  ))}
                </div>
                <div className="flex gap-4 mt-3 pt-3 border-t border-cyan-500/20">
                  {[{ label: "Close", val: insights.stats.close, color: "text-orange-400" }, { label: "Regular", val: insights.stats.regular, color: "text-emerald-400" }, { label: "Distant", val: insights.stats.distant, color: "text-muted-foreground" }].map((s) => (
                    <div key={s.label} className="text-center">
                      <span className={`text-base font-semibold ${s.color}`}>{s.val}</span>
                      <span className="text-[10px] text-muted-foreground block">{s.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Reminders */}
            {reminders.length > 0 && (
              <div data-testid="reminders-section">
                <h2 className="text-sm font-semibold text-foreground mb-3">Reconnect</h2>
                <div className="space-y-2">
                  {reminders.map((r, i) => (
                    <div key={i} className="bg-card border border-border/60 rounded-xl px-4 py-3 flex items-center gap-3">
                      <UserAvatar name={r.display_name} username={r.username} size="sm" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-foreground">{r.display_name}</p>
                        <p className="text-[11px] text-muted-foreground">{r.message}</p>
                      </div>
                      <Link to={`/profile/${r.username}`} className="text-[11px] font-medium text-cyan-400 no-underline hover:text-cyan-300 px-3 py-1 bg-cyan-500/10 rounded-full">Visit</Link>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* All Relationships */}
            <div data-testid="relationships-list">
              <h2 className="text-sm font-semibold text-foreground mb-3">All Connections ({relationships.length})</h2>
              {relationships.length === 0 ? (
                <p className="text-center text-xs text-muted-foreground/70 py-8">Start interacting with people to build connections</p>
              ) : (
                <div className="space-y-2">
                  {relationships.map((r) => {
                    const s = STRENGTH_ICONS[r.relationship_strength] || STRENGTH_ICONS.new;
                    return (
                      <div key={r.target_user_id} className="bg-card border border-border/60 rounded-xl px-4 py-3 flex items-center gap-3" data-testid={`rel-${r.username}`}>
                        <UserAvatar name={r.display_name} username={r.username} avatarUrl={r.avatar_url} size="sm" />
                        <div className="flex-1 min-w-0">
                          <Link to={`/profile/${r.username}`} className="text-xs font-semibold text-foreground no-underline hover:text-cyan-400">{r.display_name}</Link>
                          <p className="text-[10px] text-muted-foreground/70">@{r.username} &middot; {r.interaction_count} interactions &middot; {formatRelativeTime(r.last_interaction_at)}</p>
                        </div>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${s.color}`}>{s.emoji} {s.label}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Mobile Bottom Tabs */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-card border-t border-border/60 h-14 flex items-center justify-around px-2">
        <Link to="/feed" className="flex flex-col items-center gap-0.5 no-underline text-muted-foreground/70"><Home className="w-5 h-5" /><span className="text-[10px]">Home</span></Link>
        <Link to="/explore" className="flex flex-col items-center gap-0.5 no-underline text-muted-foreground/70"><Search className="w-5 h-5" /><span className="text-[10px]">Explore</span></Link>
        <Link to="/feed" className="flex flex-col items-center gap-0.5 no-underline text-muted-foreground/70"><Plus className="w-5 h-5" /></Link>
        <Link to="/notifications" className="flex flex-col items-center gap-0.5 no-underline text-muted-foreground/70"><Bell className="w-5 h-5" /><span className="text-[10px]">Notifs</span></Link>
        <Link to={profile ? `/profile/${profile.username}` : "/feed"} className="flex flex-col items-center gap-0.5 no-underline text-muted-foreground/70"><User className="w-5 h-5" /><span className="text-[10px]">Profile</span></Link>
      </div>
    </div>
  );
}
